import React from 'react';

import * as Styled from './StyledContratos';

// Formats the contractInfo.valueRange tuple into a human label.
function formatValue(t, valueRange, currency) {
    if (!Array.isArray(valueRange) || valueRange.length === 0) return null;
    const cur = currency || 'USD';
    const fmt = n => {
        try {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: cur,
                maximumFractionDigits: 0,
            }).format(n);
        } catch (e) {
            return `${cur} ${n}`;
        }
    };

    if (valueRange.length === 1 && valueRange[0] === 0) return t('card.valueNotDeclared');
    if (valueRange.length === 2 && valueRange[0] === 0 && valueRange[1] === 0)
        return t('card.valueNoPayment');
    if (valueRange.length === 1) return `> ${fmt(valueRange[0])}`;
    return `${fmt(valueRange[0])} – ${fmt(valueRange[1])}`;
}

function formatPeriod(startDate, endDate) {
    const f = d =>
        d ? new Date(d).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) : '—';
    if (!startDate && !endDate) return null;
    return `${f(startDate)} – ${f(endDate)}`;
}

export function ContratoCard({ t, data, readOnly }) {
    const [confirming, setConfirming] = React.useState(null); // 'confirmed' | 'rejected' | null
    const [busy, setBusy] = React.useState(false);

    // The counterparty's own side is the one they validate on; the OTHER side
    // is the party they are confirming a relationship with.
    const isCompanySide = data.side === 'company';
    const other = isCompanySide ? data.startup : data.company;
    const self = isCompanySide ? data.company : data.startup;

    const value = formatValue(t, data.valueRange, data.valueRangeCurrency);
    const period = formatPeriod(data.startDate, data.endDate);
    const typeLabel = data.contractType
        ? t(`contractType.${data.contractType}`, { defaultValue: data.contractType })
        : null;

    const statusLabel =
        data.status === 'confirmed'
            ? t('card.statusConfirmed')
            : data.status === 'rejected'
            ? t('card.statusRejected')
            : t('card.statusPending');

    const run = status => {
        if (busy) return;
        setBusy(true);
        Promise.resolve(data.onAction(data, status)).finally(() => {
            setBusy(false);
            setConfirming(null);
        });
    };

    return (
        <Styled.Card>
            <Styled.CardHead>
                {other.logo ? (
                    <img className="logo" src={other.logo} alt="" />
                ) : (
                    <div className="logo" />
                )}
                <div className="who">
                    <div className="org">{other.name || other.contactName || '—'}</div>
                    {other.contactName && <div className="vs">{other.contactName}</div>}
                </div>
                <Styled.StatusBadge kind={data.status}>{statusLabel}</Styled.StatusBadge>
            </Styled.CardHead>

            {typeLabel && (
                <Styled.Row>
                    <span className="label">{t('card.type')}</span>
                    <span className="value">{typeLabel}</span>
                </Styled.Row>
            )}
            {data.description && (
                <Styled.Row>
                    <span className="label">{t('card.description')}</span>
                    <span className="value">{data.description}</span>
                </Styled.Row>
            )}
            {period && (
                <Styled.Row>
                    <span className="label">{t('card.period')}</span>
                    <span className="value">{period}</span>
                </Styled.Row>
            )}
            {value && (
                <Styled.Row>
                    <span className="label">{t('card.value')}</span>
                    <span className="value">{value}</span>
                </Styled.Row>
            )}
            <Styled.Row>
                <span className="label">{t('card.parties')}</span>
                <span className="value">
                    {(self.name || self.contactEmail || '—') +
                        '  ·  ' +
                        (other.name || other.contactEmail || '—')}
                </span>
            </Styled.Row>
            {data.regDate && (
                <Styled.Row>
                    <span className="label">{t('card.regDate')}</span>
                    <span className="value">{new Date(data.regDate).toLocaleDateString()}</span>
                </Styled.Row>
            )}

            {!readOnly && !confirming && (
                <Styled.Actions>
                    <button
                        type="button"
                        className="validate"
                        disabled={busy}
                        onClick={() => setConfirming('confirmed')}
                    >
                        {t('card.validate')}
                    </button>
                    <button
                        type="button"
                        className="reject"
                        disabled={busy}
                        onClick={() => setConfirming('rejected')}
                    >
                        {t('card.reject')}
                    </button>
                </Styled.Actions>
            )}

            {!readOnly && confirming && (
                <>
                    <Styled.ConfirmText>
                        {confirming === 'confirmed'
                            ? t('card.confirmValidate')
                            : t('card.confirmReject')}
                    </Styled.ConfirmText>
                    <Styled.Actions>
                        <button
                            type="button"
                            className="confirm"
                            disabled={busy}
                            onClick={() => run(confirming)}
                        >
                            {t('card.yes')}
                        </button>
                        <button
                            type="button"
                            className="cancel"
                            disabled={busy}
                            onClick={() => setConfirming(null)}
                        >
                            {t('card.no')}
                        </button>
                    </Styled.Actions>
                </>
            )}
        </Styled.Card>
    );
}
export default ContratoCard;
